import { createSlice } from '@reduxjs/toolkit';
import { isArray } from 'lodash-es';

import { DOCUMENT_SECTION_ORDER } from '@/constants/document';

import { AccountTypeDetails } from '@/types/api/account';
import { Holder } from '@/types/api/operation';
import { PEPQuestion } from '@/types/api/pep';
import {
  Document,
  DocumentSection,
  DocumentType,
  RequiredDocument,
  RequiredDocumentSection,
} from '@/types/document';
import { FetchedFinancialInfo } from '@/types/finance';
import { User } from '@/types/user';

import { compact } from '@/utils/collection';
import {
  findMissingPersonalDetailsDocuments,
  getRequiredDocumentBySection,
  getRequiredDocumentSectionDetails,
  getUserDocumentsBySection,
} from '@/utils/document';

import {
  fetchAccountTypes,
  fetchPEPQuestions,
  fetchUserHolders,
  resetUserData,
  setFinancialInfo,
  setUserData,
} from './actions';

export interface UserDataState {
  user?: User;
  accountTypes: AccountTypeDetails[];
  accountTypeById: Record<string, AccountTypeDetails>;
  pepQuestions: PEPQuestion[];
  requiredDocumentSections: RequiredDocumentSection[];
  rawRequiredSections: RequiredDocumentSection[];
  requiredDocumentBySection: Partial<
    Record<DocumentSection, Partial<Record<DocumentType, RequiredDocument>>>
  >;
  documentsBySection: Record<string, Document[]>;
  documentByDocType: Record<string, Record<string, Document>>;
  initialized: {
    accountTypes: boolean;
    pepQuestions: boolean;
  };
  holders?: Holder[];
  fetchingUserHolders?: boolean;
  financialInfo: FetchedFinancialInfo[];
}

export const INITIAL_USER_DATA_STATE: UserDataState = {
  accountTypes: [],
  accountTypeById: {},
  pepQuestions: [],
  holders: [],
  requiredDocumentSections: [],
  rawRequiredSections: [],
  requiredDocumentBySection: {},
  documentsBySection: {},
  documentByDocType: {},
  fetchingUserHolders: false,
  financialInfo: [],

  initialized: {
    accountTypes: false,
    pepQuestions: false,
  },
};

export const userDataSlice = createSlice({
  name: 'user-data',
  initialState: INITIAL_USER_DATA_STATE,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(fetchAccountTypes.fulfilled, (state, { payload }) => {
      state.accountTypes = payload;
      state.accountTypeById = payload.reduce(
        (acc, type) => ({
          ...acc,
          [type.id]: type,
        }),
        {},
      );
    });

    builder.addCase(fetchPEPQuestions.fulfilled, (state, { payload }) => {
      state.pepQuestions = payload;
    });
    builder.addCase(fetchUserHolders.fulfilled, (state, { payload }) => {
      state.holders = payload;
    });
    builder.addCase(fetchUserHolders.pending, state => {
      state.fetchingUserHolders = true;
    });
    builder.addCase(
      setUserData,
      (state, { payload: { user, requiredSections } }) => {
        state.user = user;
        const normalizedRequiredSections = requiredSections.map(section => ({
          ...section,
          docs: isArray(section.docs) ? section.docs : [],
        }));
        state.rawRequiredSections = normalizedRequiredSections;

        if (!user) {
          state.documentsBySection = {};
        } else {
          const { documentByDocType, documentsBySection } =
            getUserDocumentsBySection(user.documents || []);
          state.documentsBySection = documentsBySection;
          state.documentByDocType = documentByDocType;

          // For the moment there will 2 types of requiredDocumentSections
          // 1. Proof of Address (POA)
          // 2. Proof of Employment (POE)
          const requiredDocumentSections = [
            ...normalizedRequiredSections,
          ].filter(item => item.type !== DocumentSection.DocumentSign);
          const missingSections = findMissingPersonalDetailsDocuments(
            requiredDocumentSections.map(item => item.type),
          );

          missingSections.forEach(section => {
            requiredDocumentSections.push({
              type: section,
              docs: [],
            });
          });

          requiredDocumentSections.sort(
            (a, b) =>
              (DOCUMENT_SECTION_ORDER[a.type] ?? 0) -
              (DOCUMENT_SECTION_ORDER[b.type] ?? 0),
          );

          const requiredDocumentBySection = getRequiredDocumentBySection(
            requiredDocumentSections,
            user,
          );
          state.requiredDocumentBySection = requiredDocumentBySection;

          const requiredDocSections = requiredDocumentSections.map(item =>
            getRequiredDocumentSectionDetails(
              item,
              requiredDocumentBySection[item.type] || {},
              user,
            ),
          );

          state.requiredDocumentSections = compact(requiredDocSections);
        }
      },
    );
    builder.addCase(resetUserData, state => {
      Object.assign(state, INITIAL_USER_DATA_STATE);
    });
    builder.addCase(setFinancialInfo, (state, { payload }) => {
      state.financialInfo = payload;
    });

    builder.addMatcher(fetchAccountTypes.settled, state => {
      state.initialized.accountTypes = true;
    });
    builder.addMatcher(fetchPEPQuestions.settled, state => {
      state.initialized.pepQuestions = true;
    });
    builder.addMatcher(fetchUserHolders.settled, state => {
      state.fetchingUserHolders = false;
    });
  },
});

export const userDataReducer = userDataSlice.reducer;
